import { api } from '@/config/api';

export type ProductFeature = 'otp' | 'transactional' | 'contact_sync' | 'daily_mcq';
export type OtpTemplateShape = 'code_only' | 'code_plus_url_button' | 'code_purpose_url_button';

/** An OTP template bound to a login context ('app_login', 'admin_login', …). */
export interface ProductOtpTemplate {
  key: string;
  label?: string;
  templateName: string;
  language: string;
  shape: OtpTemplateShape;
}

export interface ProductStats {
  otpSent30d: number;
  transactionalSent30d: number;
  failed30d: number;
  spark: number[];
  lastSentAt: string | null;
  contacts: number;
}

export interface Product {
  productUid: string;
  companyUid: string;
  key: string;
  name: string;
  description?: string;
  iconUrl?: string;
  websiteUrl?: string;
  playStoreUrl?: string;
  appStoreUrl?: string;
  whatsappAccountUid: string;
  otpTemplateName: string;
  otpTemplateLanguage: string;
  otpTemplateShape: OtpTemplateShape;
  /** Extra contexts beyond the default app login. */
  otpTemplates?: ProductOtpTemplate[];
  features: ProductFeature[];
  contactTag: string;
  contactGroupUid?: string;
  credentialUids?: string[];
  aiEnabled?: boolean;
  /** 'proxy' = provider keys never leave the CRM; the app calls AI through it. */
  aiDelivery?: 'env' | 'proxy';
  aiProxyRequests?: number;
  lastAiProxyAt?: string | null;
  lastConfigFetchAt?: string | null;
  configFetchCount?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  activeKeys?: number;
  stats?: ProductStats;
}

export interface ProductPayload {
  name: string;
  key?: string;
  description?: string;
  iconUrl?: string;
  websiteUrl?: string;
  playStoreUrl?: string;
  appStoreUrl?: string;
  whatsappAccountUid: string;
  otpTemplateName: string;
  otpTemplateLanguage?: string;
  otpTemplateShape?: OtpTemplateShape;
  otpTemplates?: ProductOtpTemplate[];
  features?: ProductFeature[];
  isActive?: boolean;
  credentialUids?: string[];
  aiEnabled?: boolean;
  aiDelivery?: 'env' | 'proxy';
}

export interface ProductApiKey {
  apiKeyUid: string; name: string; prefix: string; scopes: string[]; isActive: boolean;
  lastUsedAt?: string; totalRequests?: number; expiresAt?: string; revokedAt?: string; createdAt: string;
}
export interface IssuedKey { apiKeyUid: string; name: string; prefix: string; scopes: string[]; plaintextKey: string; createdAt: string; }

const BASE = '/slighter_crm/products';

export const productsService = {
  list: async (): Promise<Product[]> => (await api.get(BASE)).data,
  create: async (p: ProductPayload): Promise<Product> => (await api.post(BASE, p)).data,
  update: async (uid: string, p: Partial<ProductPayload>): Promise<Product> => (await api.patch(`${BASE}/${uid}`, p)).data,
  remove: async (uid: string): Promise<void> => { await api.delete(`${BASE}/${uid}`); },
  stats: async (uid: string): Promise<ProductStats> => (await api.get(`${BASE}/${uid}/stats`)).data,
  listKeys: async (uid: string): Promise<ProductApiKey[]> => (await api.get(`${BASE}/${uid}/api-keys`)).data,
  issueKey: async (uid: string, body: { name: string; scopes?: string[] }): Promise<IssuedKey> => (await api.post(`${BASE}/${uid}/api-keys`, body)).data,
  revokeKey: async (uid: string, apiKeyUid: string): Promise<void> => { await api.delete(`${BASE}/${uid}/api-keys/${apiKeyUid}`); },
};
